import { useIntervalFn } from '@vueuse/core';
import { storeToRefs } from 'pinia';
import { computed, nextTick, ref } from 'vue';
import { useIdle } from '@/composable/useIdle';
import { useState } from '@/composable/useState';
import { assignArray, remove, upsert } from '@/core/lib/array';
import BusinessLogicHelper from '@/core/lib/businessLogicHelper';
import { deepFreeze } from '@/core/lib/utils';
import { formatOutrightOdds } from '@/core/oddsApi/helpers/formatOutrightOdds';
import { useOutrightsQuery, useOutrightsSubscription } from '@/core/oddsApi/oddsApiType';
import type { IOutrightOdds } from '@/interface/IOutrightOdds';
import { SportType } from '@/interface/enum';
import { useOddsApiStore } from '@/store/oddsApiStore';

export function useOutrightOddsEuro(
    sportType: SportType,
    leagueId: number,
    isLive: boolean,
    isActive = ref(true),
) {
    const oddsApiSportType = SportType.toOddsApi(sportType);

    const variables = computed(() => ({
        query: {
            sport: oddsApiSportType,
            tournamentId: leagueId,
            isLive,
        },
    }));

    const [isEnabled, setIsEnabled] = useState(true);
    const refetch = async () => {
        if (isEnabled.value) {
            setIsEnabled(false);
            nextTick(() => setIsEnabled(true));
        }
    };
    const { loaded, loading, onResult } = useOutrightsQuery(
        variables,
        computed(() => ({
            enabled: isActive.value && isEnabled.value,
        })),
    );

    const { onSubscription } = useOutrightsSubscription(
        variables,
        computed(() => ({
            enabled: isActive.value,
        })),
    );

    const outrightOdds = ref<IOutrightOdds[]>([]);
    const { customerSettings } = storeToRefs(useOddsApiStore());

    onResult((result) => {
        const newOutrightOdds = (result ?? [])
            .map(x => ({
                ...x,
                tournament: {
                    sport: {
                        sportType: oddsApiSportType,
                    },
                },
            }))
            .filter(x => BusinessLogicHelper.isOutrightAllowed(customerSettings.value, x))
            .map(odds => deepFreeze(formatOutrightOdds(odds)));
        assignArray(outrightOdds.value, newOutrightOdds, x => x.id);
    });

    onSubscription((subscription) => {
        const toUpdate = (subscription?.updated ?? [])
            .map(x => ({
                ...x,
                tournament: {
                    sport: {
                        sportType: oddsApiSportType,
                    },
                },
            }))
            .filter(x => BusinessLogicHelper.isOutrightAllowed(customerSettings.value, x))
            .map(odds => deepFreeze(formatOutrightOdds(odds)));

        upsert(outrightOdds.value, toUpdate, item => item.id);
        remove(outrightOdds.value, subscription?.deleted ?? [], (a, b) => a.id === b);
    });

    useIntervalFn(refetch, 120 * 1000);

    // Automatically recover from idle state with a force refresh
    // This is to prevent the user from losing data when they are idle
    const { onActive } = useIdle(60 * 1000);
    onActive(() => {
        // eslint-disable-next-line no-console
        console.log('useOutrightOddsEuro: Recovering from idle state...');
        refetch();
    });

    return {
        loaded,
        loading,
        outrightOdds,
        refetch,
    };
}
